import type { GroundControlConfigApi } from 'api';
import {
  AccountBalance,
  AddBox,
  Addchart,
  Article,
  AttachMoney,
  AutoAwesome,
  Badge,
  BarChart,
  Business,
  Calculate,
  CameraAlt,
  CameraEnhance,
  DirectionsBoat,
  DocumentScanner,
  FindInPage,
  Help,
  HotTub,
  Inventory,
  LocalShipping,
  ManageSearchRounded,
  MenuBook,
  MonetizationOn,
  PeopleOutline,
  Person,
  PersonAddAlt,
  PersonSearch,
  PhotoCameraBackOutlined,
  PointOfSale,
  PostAdd,
  RequestQuote,
  Settings,
  ShoppingCartCheckout,
  StackedLineChart,
  SyncOutlined,
  TrendingUp,
} from 'ui/icons';

const JIRA_SUPPORT_PORTAL_URL = 'https://safi.atlassian.net/servicedesk/customer/portals';
export interface TabLink {
  description: string;
  href: string;
  icon: JSX.Element;
  /**
   * Whether the link is internal or external.
   * Internal links use the `Link` component from `react-router-dom` for improved performance.
   * External links use the `a` tag.
   */
  isLegacy: boolean;
  label: string;
}

export interface Tab {
  icon: JSX.Element;
  label: string;
  links: TabLink[];
}

export const getTabs = (settings?: GroundControlConfigApi): Tab[] => [
  {
    icon: <TrendingUp />,
    label: 'Trade',
    links: [
      {
        description: 'Create a demand signals. It will be synced with Hubspot',
        href: '/ground-control/trade-signals/demand/create/',
        icon: <Addchart />,
        isLegacy: false,
        label: 'Create Demand Signal',
      },
      {
        description: 'Create a supply signal. It will be synced with Hubspot',
        href: '/ground-control/trade-signals/supply/create/',
        icon: <StackedLineChart />,
        isLegacy: false,
        label: 'Create Supply Signal',
      },
      {
        description: 'Create a Purchase Order and send it to the seller for signing',
        href: '/ground-control/create-purchase-order/',
        icon: <ShoppingCartCheckout />,
        isLegacy: false,
        label: 'Create Purchase Order',
      },
      {
        description: 'Create a Sales Order and send it to the buyer for signing',
        href: '/ground-control/v2/create-sales-order/',
        icon: <AttachMoney />,
        isLegacy: false,
        label: 'Create Sales Order',
      },
      {
        description: 'View, edit and match',
        href: '/ground-control/purchase_orders/?match_status=0+UN&match_status=4+PA',
        icon: <Inventory />,
        isLegacy: true,
        label: 'View Purchase Orders',
      },
      {
        description: 'View, edit and match',
        href: '/ground-control/sales_orders/?match_status=0+UN&match_status=4+PA',
        icon: <PostAdd />,
        isLegacy: true,
        label: 'View Sales Orders',
      },
      {
        description: 'Calculate deal profitability',
        href: '/ground-control/profitability-calculator/',
        icon: <Calculate />,
        isLegacy: false,
        label: 'Deal Profitability Calculator',
      },
      {
        description: 'Link to Transport Metabase',
        href: 'https://metabase.internal.safi.co/dashboard/39-transport-quote?am=&carrier=&delivery_country=&delivery_location=&equipment_type=&expired_on_date=&incoterm=&load_country=&load_location=&pod=&pol=&quoted_on_date=&transport_provider=&type_of_goods=',
        icon: <DirectionsBoat />,
        isLegacy: true,
        label: 'Transport Metabase',
      },
      {
        description: 'Add quality Report Data and Images',
        href: '/quality/',
        icon: <CameraEnhance />,
        isLegacy: false,
        label: 'Quality Report',
      },
      {
        description: 'Find matches, send offers and flag to other AMs',
        href: '/ground-control/trading/',
        icon: <AutoAwesome />,
        isLegacy: false,
        label: 'Trading',
      },
      {
        description: 'Generate documents for Purchase Orders, Sales Orders, Shipments, etc.',
        href: '/ground-control/document-generation/',
        icon: <DocumentScanner />,
        isLegacy: false,
        label: 'Generate Documents',
      },
    ],
  },
  {
    icon: <Person />,
    label: 'Users & companies',
    links: [
      {
        description: 'Create a user and new company, or invite to an existing company',
        href: '/ground-control/user_onboarding/',
        icon: <PersonAddAlt />,
        isLegacy: true,
        label: 'Create user and company',
      },
      {
        description: 'Quickly hijack a user account',
        href: '/ground-control/admin/accounts/customuser/',
        icon: <HotTub />,
        isLegacy: true,
        label: 'Hijack a user',
      },
      {
        description: 'View all users in HubSpot',
        href:
          settings?.hubspot_contacts_view_url ||
          'https://app-eu1.hubspot.com/contacts/25870036/objects/0-1/views/21699080/list',
        icon: <PeopleOutline />,
        isLegacy: true,
        label: 'View all users',
      },
      {
        description: 'View and edit supply & demand trading companies',
        href: '/ground-control/admin/trade_companies/tradecompany/',
        icon: <Business />,
        isLegacy: true,
        label: 'View all trade companies',
      },
      {
        description: 'Create a new admin user and make them a member of Safi',
        href: '/ground-control/admin_onboarding/',
        icon: <Badge />,
        isLegacy: true,
        label: 'Staff Onboarding',
      },
    ],
  },
  {
    icon: <Article />,
    label: 'Contracts',
    links: [
      {
        description: 'Create a Purchase Order and send it to the seller for signing',
        href: '/ground-control/create-purchase-order/',
        icon: <ShoppingCartCheckout />,
        isLegacy: false,
        label: 'Create Purchase Order',
      },
      {
        description: 'View, edit and match',
        href: '/ground-control/purchase_orders/?match_status=0+UN&match_status=4+PA',
        icon: <Inventory />,
        isLegacy: true,
        label: 'View Purchase Orders',
      },
      {
        description: 'Create a Sales Order and send it to the buyer for signing',
        href: '/ground-control/v2/create-sales-order/',
        icon: <AttachMoney />,
        isLegacy: false,
        label: 'Create Sales Order',
      },
      {
        description: 'View, edit and match',
        href: '/ground-control/sales_orders/?match_status=0+UN&match_status=4+PA',
        icon: <PostAdd />,
        isLegacy: true,
        label: 'View Sales Orders',
      },
    ],
  },
  {
    icon: <PointOfSale />,
    label: 'Deals',
    links: [
      {
        description: 'View and action deal tasks',
        href: '/post-deal/dealhub/',
        icon: <Settings />,
        isLegacy: true,
        label: 'Go To DealHub',
      },
      {
        description: 'View and edit all deals and loads',
        href: '/post-deal/dealhub/deal/list/',
        icon: <RequestQuote />,
        isLegacy: true,
        label: 'View All Deals',
      },
    ],
  },
  {
    icon: <MonetizationOn />,
    label: 'Finance',
    links: [
      {
        description: 'Create, edit and view costs',
        href: '/ground-control/admin/finance/cost/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: 'Manage Costs',
      },
      {
        description: 'Create, edit and view Revenue',
        href: '/ground-control/admin/finance/revenue/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: 'Manage Revenue',
      },
      {
        description: 'Create Shipping Rate and all associated details',
        href: '/ground-control/create_shipping_rate/',
        icon: <DirectionsBoat />,
        isLegacy: true,
        label: 'Create Shipping Rate',
      },
      {
        description: 'View and edit Bank Details',
        href: '/ground-control/admin/accounts/bankdetails/',
        icon: <AccountBalance />,
        isLegacy: true,
        label: 'Manage Banks',
      },
      {
        description: 'Create, edit and view External Company Details',
        href: '/ground-control/admin/finance/externalcompanydetails/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: 'Manage External Companies',
      },
      {
        description: 'Calculate deal profitability',
        href: '/ground-control/profitability-calculator/',
        icon: <Calculate />,
        isLegacy: false,
        label: 'Deal Profitability Calculator',
      },
      {
        description: 'View historical PERN prices from T2E',
        href: '/ground-control/admin/common/pernprice/',
        icon: <BarChart />,
        isLegacy: true,
        label: 'Market PERN Prices',
      },
      {
        description: 'Submit Payment Requests to Finmid',
        href: '/ground-control/financing-home/',
        icon: <ShoppingCartCheckout />,
        isLegacy: false,
        label: 'Manage Financing',
      },
    ],
  },
  {
    icon: <LocalShipping />,
    label: 'Transport',
    links: [
      {
        description: 'Create, edit and view Carriers',
        href: '/ground-control/admin/tracker/carrier/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: 'Manage Carriers',
      },
      {
        description: 'View all Ports',
        href: '/ground-control/admin/accounts/port/',
        icon: <DirectionsBoat />,
        isLegacy: true,
        label: 'View Ports',
      },
      {
        description: 'Sync transport costs from spreadsheet',
        href: '/ground-control/sync-transport-costs/',
        icon: <SyncOutlined />,
        isLegacy: true,
        label: 'Sync Transport Costs',
      },
    ],
  },
  {
    icon: <FindInPage />,
    label: 'Inspections',
    links: [
      {
        description: 'View and edit inspections',
        href: '/ground-control/inspections/',
        icon: <FindInPage />,
        isLegacy: false,
        label: 'Inspections',
      },
      {
        description: 'Create a new inspection',
        href: '/ground-control/inspections/create/',
        icon: <AddBox />,
        isLegacy: false,
        label: 'Create Inspection',
      },
      {
        description: 'View and edit upcoming loads',
        href: '/ground-control/inspections/loads/',
        icon: <LocalShipping />,
        isLegacy: false,
        label: 'View Upcoming Loads',
      },
      {
        description: 'View and edit inspection companies',
        href: '/ground-control/inspections/companies/',
        icon: <Business />,
        isLegacy: false,
        label: 'Inspection companies',
      },
      {
        description: 'View and edit inspectors',
        href: '/ground-control/inspections/inspectors/',
        icon: <PersonSearch />,
        isLegacy: false,
        label: 'Inspectors',
      },
      {
        description: 'Add new site evidence',
        href: '/quality/',
        icon: <CameraAlt />,
        isLegacy: false,
        label: 'Add Site Evidence',
      },
      {
        description: 'Add evidence from external sources',
        href: '/quality/external-evidence/',
        icon: <PhotoCameraBackOutlined />,
        isLegacy: false,
        label: 'Add External Evidence',
      },
    ],
  },
  {
    icon: <Settings />,
    label: 'Advanced',
    links: [
      {
        description: 'Update PERN types',
        href: '/ground-control/admin/materials/perntype/',
        icon: <MonetizationOn />,
        isLegacy: true,
        label: 'Pern Type',
      },
      {
        description: 'View and edit Material Products',
        href: '/ground-control/material-products/',
        icon: <ManageSearchRounded />,
        isLegacy: false,
        label: 'Manage Material Products',
      },
    ],
  },
  {
    icon: <Help />,
    label: 'Support',
    links: [
      {
        description: 'Help guides',
        href: '/help',
        icon: <MenuBook />,
        isLegacy: false,
        label: 'Help & learn',
      },
      {
        description: 'Create a Jira support issue',
        href: JIRA_SUPPORT_PORTAL_URL,
        icon: <Help />,
        isLegacy: true,
        label: 'Get Support',
      },
    ],
  },
];
